import { useConfig } from '@exam-management/configuration';
import {
  type GetQBankStructureLevelItemsByQBankStructureIdQuery,
  type GetQBankStructureLevelsByQBankStructureIdQuery,
  useSearchQuestionGroupsLazyQuery,
  useSearchQuestionsForQuestionReviewTaskLazyQuery,
} from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
} from '@shared/components';
import { useEffect, useMemo, useRef, useState } from 'react';

import { EMPTY_LOCATION, EMPTY_QUESTION } from '../../constants/form-defaults';
import { type QuestionType } from '../../constants/question-selection';
import { useQuestionReviewTaskCreationFormContext } from '../../providers';
import { AddQuestionList } from '../add-question-list';
import type { Question } from '../add-question-list/columns';
import { SelectLocationFilter } from '../select-location-filter';

import {
  buildQuestionGroupRows,
  buildQuestionRows,
  createQuestionGroupSearchVariables,
  createQuestionSearchVariables,
  createRequestKey,
  getActiveLevelItemId,
} from './question-fetching';

interface AddQuestionModalProps {
  levelItemId: string;
  qbsLevelItem: GetQBankStructureLevelItemsByQBankStructureIdQuery;
  qbsLevels: GetQBankStructureLevelsByQBankStructureIdQuery;
  open: boolean;
  onClose: () => void;
}

interface AddQuestionModalContentProps {
  qbsLevelItem: GetQBankStructureLevelItemsByQBankStructureIdQuery;
  qbsLevels: GetQBankStructureLevelsByQBankStructureIdQuery;
  questionType: QuestionType;
  setQuestionType: (questionType: QuestionType) => void;
  rows: Question[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

interface UseQuestionModalRowsOptions {
  open: boolean;
  questionType: QuestionType;
  courseId: string;
  levelItemId: string;
  internalUserEmailDomains: string[];
  currentPage: number;
  pageSize: number;
}

function AddQuestionModalContent(props: Readonly<AddQuestionModalContentProps>) {
  const {
    qbsLevelItem,
    qbsLevels,
    questionType,
    setQuestionType,
    rows,
    loading,
    total,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onCancel,
    onConfirm,
  } = props;

  return (
    <>
      <DialogTitle onClose={onCancel}>เลือกคำถามสำหรับทบทวน</DialogTitle>
      <Divider />
      <DialogContent>
        <Flex
          sx={{
            flex: 1,
            gap: (theme) => theme.spacing(4),
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          <SelectLocationFilter
            qbsLevelItems={qbsLevelItem}
            qbsLevel={qbsLevels}
            questionType={questionType}
            setQuestionType={setQuestionType}
          />
          <AddQuestionList
            rows={rows}
            total={total}
            loading={loading}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onCancel} type='button' color='secondary' variant='outlined'>
          Cancel
        </Button>
        <Button onClick={onConfirm} type='button'>
          Confirm
        </Button>
      </DialogActions>
    </>
  );
}

function useQuestionModalRows(options: Readonly<UseQuestionModalRowsOptions>) {
  const {
    open,
    questionType,
    courseId,
    levelItemId,
    internalUserEmailDomains,
    currentPage,
    pageSize,
  } = options;

  const latestRequestKeyRef = useRef('');
  const [rows, setRows] = useState<Question[]>([]);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const [searchQuestions, { loading: isSearchQuestionsLoading }] =
    useSearchQuestionsForQuestionReviewTaskLazyQuery();

  const [searchQuestionGroups, { loading: isSearchQuestionGroupsLoading }] =
    useSearchQuestionGroupsLazyQuery();

  useEffect(() => {
    if (!open || !courseId || !levelItemId) {
      return;
    }

    const requestKey = createRequestKey({
      questionType,
      courseId,
      levelItemId,
    });

    latestRequestKeyRef.current = requestKey;
    setIsFetching(true);

    if (questionType === 'item-set') {
      searchQuestionGroups({
        variables: createQuestionGroupSearchVariables({
          courseId,
          levelItemId,
          currentPage,
          pageSize,
        }),
        fetchPolicy: 'network-only',
      })
        .then(({ data }) => {
          if (latestRequestKeyRef.current !== requestKey) {
            return;
          }

          setTotal(data?.qctSearchQuestionGroups?.total ?? 0);
          setRows(
            buildQuestionGroupRows({
              data,
              internalUserEmailDomains,
            }),
          );
        })
        .finally(() => {
          if (latestRequestKeyRef.current === requestKey) {
            setIsFetching(false);
          }
        });

      return;
    }

    searchQuestions({
      variables: createQuestionSearchVariables({
        courseId,
        levelItemId,
        currentPage,
        pageSize,
      }),
      fetchPolicy: 'network-only',
    })
      .then(({ data }) => {
        if (latestRequestKeyRef.current !== requestKey) {
          return;
        }

        setTotal(data?.qctSearchQuestions?.total ?? 0);
        setRows(
          buildQuestionRows({
            data,
            internalUserEmailDomains,
          }),
        );
      })
      .finally(() => {
        if (latestRequestKeyRef.current === requestKey) {
          setIsFetching(false);
        }
      });
  }, [
    courseId,
    currentPage,
    internalUserEmailDomains,
    levelItemId,
    open,
    pageSize,
    questionType,
    searchQuestionGroups,
    searchQuestions,
  ]);

  return {
    rows,
    total,
    loading: isFetching || isSearchQuestionsLoading || isSearchQuestionGroupsLoading,
  };
}

export default function AddQuestionModal(props: Readonly<AddQuestionModalProps>) {
  const { open, onClose, levelItemId, qbsLevelItem, qbsLevels } = props;
  const configuration = useConfig();
  const internalUserEmailDomains = configuration.modules.userManagement.internalUserEmailDomains;

  const formContext = useQuestionReviewTaskCreationFormContext();
  const location = formContext.watch('location');
  const courseId = formContext.watch('courseId')?.value ?? '';
  const question = formContext.watch('question') ?? EMPTY_QUESTION;

  const [previousValue, setPreviousValue] = useState(question);
  const [questionType, setQuestionType] = useState<QuestionType>('single');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPreviousValue(question);
  }, [open]);

  const activeLevelItemId = useMemo(
    () =>
      getActiveLevelItemId({
        location,
        defaultLevelItemId: levelItemId,
      }),
    [
      levelItemId,
      location.SUB_TOPIC,
      location.TOPIC,
      location.CHAPTER,
      location.GROUP,
      location.SUBJECT,
      location.PART,
    ],
  );

  const { rows, total, loading } = useQuestionModalRows({
    open,
    questionType,
    courseId,
    levelItemId: activeLevelItemId,
    internalUserEmailDomains,
    currentPage,
    pageSize,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPage(0);
    setPageSize(newPageSize);
  };

  const handleCancel = () => {
    formContext.setValue('question', previousValue);
    formContext.setValue('location', EMPTY_LOCATION);
    setQuestionType('single');
    setCurrentPage(0);
    setPageSize(10);
    onClose();
  };

  const handleConfirm = () => {
    onClose();
  };

  const handleQuestionTypeChange = (newType: QuestionType) => {
    setQuestionType(newType);
    setCurrentPage(0);
  };

  return (
    <Dialog open={open} maxWidth='xl' onClose={handleCancel}>
      <AddQuestionModalContent
        qbsLevelItem={qbsLevelItem}
        qbsLevels={qbsLevels}
        questionType={questionType}
        setQuestionType={handleQuestionTypeChange}
        rows={rows}
        loading={loading}
        total={total}
        page={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </Dialog>
  );
}
